import { Button, Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Modal, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { MagnifyingGlass, Plus } from 'phosphor-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fget, fpost } from '../../../API/callsAPI';
import DeleteModal from '../../Utilities/DeleteModal/DeleteModal.component';
import Loader from '../../Utilities/Loader/Loader';
import ChitranSvg from '../../Utilities/Svg/ChitranSvg';
import AddApiKeyModal from './AddApiKeyModal';
import useStyles from './ApiKeysSettingsOverview.style';
import ApiKeyTableItem from './ApiKeyTableItem';
import CopyApiKeyModal from './CopyApiKeyModal';


export default function ApiKeysSettingsOverview() {
    const classes = useStyles();
    const params = useParams();
    const [nameSearchQuery, setNameSearchQuery] = useState("");
    const [addApiKeyModalOpen, setAddApiKeyModalOpen] = useState(false);
    const [editApiKeyModal, setEditApiKeyModal] = useState({ open: false, data: null });
    const [deleteApiKeyModal, setDeleteApiKeyModal] = useState({ open: false, data: null });
    const [copyApiKeyModal, setCopyApiKeyModal] = useState({ open: false, apiKey: "" });
    const [apiKeys, setApiKeys] = useState([]);
    const [snackValue, setSnackValue] = useState({ isOpen: false, message: "", isError: false });
    const [isApiKeysLoading, setIsApiKeysLoading] = useState(false);
    const [refreshCounter, setRefreshCounter] = useState(0);

    const getApiKeys = async () => {
        setIsApiKeysLoading(true);
        try {
            const data = await fget({ url: `api-key/?projectId=${params.projectId}` });
            if (data.status === 200 || data.status === 201) {
                const APIKeysData = data.data.filter((apiKey) => !apiKey.revoked);
                setApiKeys(APIKeysData);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsApiKeysLoading(false);
        }
    }

    useEffect(() => {
        getApiKeys();
    }, [refreshCounter]);

    const updateRefreshCounter = () => {
        setRefreshCounter(refreshCounter + 1);
    }

    const handleDeleteApiKey = async () => {
        const apiKey = deleteApiKeyModal.data;
        try {
            const data = await fpost({ url: `api-key/${apiKey.uuid}/revoke/` });
            if (data.status === 200 || data.status === 201) {
                setSnackValue({ isOpen: true, message: "API Key deleted successfully", isError: false });
            }
        } catch (error) {
            console.log(error);
        } finally {
            updateRefreshCounter();
            setDeleteApiKeyModal({ open: false, data: null });
        }
    }

    return (
        <>
            <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                open={snackValue.isOpen}
                autoHideDuration={2000}
                onClose={() => setSnackValue({ isOpen: false, message: "" })}
            >
                <Alert severity={snackValue.isError === false ? "success" : "warning"}>
                    {snackValue.message}
                </Alert>
            </Snackbar>
            <div className={classes.flexBetween}>
                <div className={classes.logoDiv}>
                    <Typography className={classes.title}>API Keys</Typography>
                    <Typography style={{ fontSize: "14px", color: "#B1B5C9" }}>{apiKeys.length}</Typography>
                </div>
                <div className={classes.logoDiv}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            backgroundColor: "#252A38",
                            borderRadius: "10px",
                            width: "200px",
                            padding: "0px 10px",
                        }}
                    >
                        <MagnifyingGlass size={20} />
                        <TextField
                            inputProps={{
                                style: {
                                    color: "#606479 !important",
                                },
                            }}
                            sx={{
                                "& .MuiInputBase-input": {
                                    color: "#fff !important",
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        border: "none",
                                    },
                                },
                            }}
                            size="small"
                            fullWidth
                            placeholder="Search in list"
                            value={nameSearchQuery}
                            onChange={(e) => setNameSearchQuery(e.target.value)}
                        />
                    </div>
                    <Button
                        startIcon={<Plus />}
                        variant="contained"
                        color="secondary"
                        onClick={() => setAddApiKeyModalOpen(true)}
                    >
                        New API Key
                    </Button>
                </div>
            </div>
            <Typography style={{ fontSize: "14px", margin: "8px 0px 16px 0px", color: "#B1B5C9" }}>
                API keys allow external applications to authenticate with your project. Keep your keys secret as anyone with a key can access your project data.
            </Typography>
            {isApiKeysLoading ?
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                }}>
                    <Loader />
                </div> : apiKeys.length === 0 ? <div style={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                    flexDirection: "column",
                    gap: "10px",
                }}>
                    <ChitranSvg />
                    <Typography style={{ fontSize: "16px" }}>
                        No API keys found
                    </Typography>
                </div> : <TableContainer>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left" className={classes.tableHeaderCell}>
                                    API Key
                                </TableCell>
                                <TableCell align="center" className={classes.tableHeaderCell}>
                                    Name
                                </TableCell>
                                <TableCell align="center" className={classes.tableHeaderCell}>
                                    Created by
                                </TableCell>
                                <TableCell align="center" className={classes.tableHeaderCell}>
                                    Created on
                                </TableCell>
                                <TableCell align="center" className={classes.tableHeaderCell}>
                                    Expiery
                                </TableCell>
                                <TableCell align="right" className={classes.tableHeaderCell}>
                                    Action
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {apiKeys.filter((apiKey) => apiKey.name.toLowerCase().includes(nameSearchQuery.toLowerCase())).map((apiKey) => (
                                <ApiKeyTableItem
                                    apiKey={apiKey}
                                    setCopyApiKeyModal={setCopyApiKeyModal}
                                    setEditApiKeyModal={setEditApiKeyModal}
                                    setDeleteApiKeyModal={setDeleteApiKeyModal}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>}

            <Modal
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                open={addApiKeyModalOpen}
                onClose={() => setAddApiKeyModalOpen(false)}
            >
                <AddApiKeyModal
                    setSnackValue={setSnackValue}
                    updateRefreshCounter={updateRefreshCounter}
                    handleClose={() => setAddApiKeyModalOpen(false)}
                />
            </Modal>

            <Modal
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                open={editApiKeyModal.open}
                onClose={() => setEditApiKeyModal({ open: false, data: null })}
            >
                <AddApiKeyModal
                    editMode={true}
                    initialData={editApiKeyModal.data}
                    handleClose={() => setEditApiKeyModal({ open: false, data: null })}
                    setSnackValue={setSnackValue}
                    updateRefreshCounter={updateRefreshCounter}
                />
            </Modal>

            <Modal
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                open={deleteApiKeyModal.open}
                onClose={() => setDeleteApiKeyModal({ open: false, data: null })}
            >
                <DeleteModal
                    type="API Key"
                    name={deleteApiKeyModal.data?.name}
                    handleCloseDeleteModal={() => setDeleteApiKeyModal({ open: false, data: null })}
                    handleDelete={
                        handleDeleteApiKey
                    }
                />
            </Modal>

            <Modal
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                open={copyApiKeyModal.open}
                onClose={() => setCopyApiKeyModal({ open: false, apiKey: "" })}
            >
                <CopyApiKeyModal
                    apiKey={copyApiKeyModal.apiKey}
                    setSnackValue={setSnackValue}
                    handleClose={() => setCopyApiKeyModal({ open: false, apiKey: "" })}
                />
            </Modal>
        </>
    )
}